// src/components/admin/TripHistoryPanel.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  X, Play, Calendar, Clock, TrendingUp, MapPin, 
  ChevronRight, ChevronDown, Filter, Download, RefreshCw,
  Gauge, Route, Activity
} from 'lucide-react';
import './TripHistoryPanel.css';

/**
 * TripHistoryPanel Component
 * 
 * Shows trip history for a selected device based on locationHistory from backend
 * Groups trips by date and allows playback of individual trips
 * 
 * @param {String} deviceId - Selected device id
 * @param {String} deviceName - Selected device name
 * @param {Function} onClose - Close panel handler
 * @param {Function} onPlayTrip - Handler when user clicks play on a trip
 */
const TripHistoryPanel = ({ 
  deviceId,
  deviceName,
  onClose, 
  onPlayTrip,
}) => {
  const [expandedDates, setExpandedDates] = useState({});
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [dateFilter, setDateFilter] = useState('all'); // 'today', 'week', 'month', 'all'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [historyLoading, setHistoryLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [dailyTrips, setDailyTrips] = useState([]);
  const [rides, setRides] = useState([]);
  const [units, setUnits] = useState({ distanceUnit: "km", speedUnit: "km/h" });

  const baseUrl = useMemo(() => {
    return (
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_API_URL ||
      "https://app.krush.in/api"
    );
  }, []);

  const getDefaultDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!deviceId) return;
    const { startDate, endDate } = getDefaultDateRange();
    setHistoryLoading(true);
    setFetchError("");
    try {
      const token = localStorage.getItem("token");
      const url = `${baseUrl}/history/${deviceId}/report?startDate=${startDate}&endDate=${endDate}`;
      const response = await fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "x-auth-token": token || "",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const list = Array.isArray(data?.dailyData) ? data.dailyData : (Array.isArray(data?.trips) ? data.trips : []);
      const rideList = Array.isArray(data?.rides) ? data.rides : [];
      setDailyTrips(list);
      setRides(rideList);
      if (data?.units) setUnits(data.units);
    } catch (err) {
      setFetchError(err?.message || "Failed to load trip history");
      setDailyTrips([]);
      setRides([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [baseUrl, deviceId, getDefaultDateRange]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const processedTrips = useMemo(() => {
    if (Array.isArray(rides) && rides.length > 0) {
      const normalized = rides.map((ride, idx) => ({
        id: String(ride?.id || `ride_${idx}`),
        date: String(ride?.date || ""),
        startTime: new Date(ride?.startTime || `${ride?.date}T00:00:00Z`),
        endTime: new Date(ride?.endTime || `${ride?.date}T23:59:59Z`),
        distance: Number(ride?.distanceRawKm ?? ride?.distance ?? 0),
        maxSpeed: Number(ride?.maxSpeedRawKmh ?? ride?.maxSpeed ?? 0),
        avgSpeed: Number(ride?.avgSpeedRawKmh ?? ride?.avgSpeed ?? 0),
        movingTimeMs: Number(ride?.movingTime || 0) * 1000,
        pointCount: Number(ride?.pointCount || 0),
      }));

      let totalDistance = 0;
      let totalTime = 0;
      let totalPoints = 0;
      normalized.forEach((trip) => {
        totalDistance += trip.distance;
        totalTime += trip.movingTimeMs;
        totalPoints += trip.pointCount;
      });

      const sortedTrips = sortOrder === 'desc'
        ? [...normalized].sort((a, b) => new Date(b.startTime) - new Date(a.startTime))
        : [...normalized].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

      return {
        trips: sortedTrips,
        totalDistance,
        totalTime,
        totalPoints,
      };
    }

    if (!Array.isArray(dailyTrips) || dailyTrips.length === 0) {
      return { trips: [], totalDistance: 0, totalTime: 0, totalPoints: 0 };
    }

    const list = dailyTrips.map((day, idx) => {
      const date = String(day?.date || "");
      const startTime = new Date(`${date}T00:00:00Z`);
      const endTime = new Date(`${date}T23:59:59Z`);
      const distanceRawKm = Number(day?.distanceRawKm ?? day?.distance ?? 0);
      const movingTimeSec = Number(day?.movingTime || 0);

      return {
        id: `trip_${date || idx}`,
        date,
        startTime,
        endTime,
        distance: distanceRawKm,
        maxSpeed: Number(day?.maxSpeedRawKmh ?? day?.maxSpeed ?? 0),
        avgSpeed: Number(day?.avgSpeedRawKmh ?? day?.avgSpeed ?? 0),
        movingTimeMs: movingTimeSec * 1000,
        pointCount: Number(day?.pointCount || 0),
      };
    });

    let totalDistance = 0;
    let totalTime = 0;
    let totalPoints = 0;
    list.forEach((trip) => {
      totalDistance += trip.distance;
      totalTime += trip.movingTimeMs;
      totalPoints += trip.pointCount;
    });

    const sortedTrips = sortOrder === 'desc'
      ? [...list].sort((a, b) => String(b.date).localeCompare(String(a.date)))
      : [...list].sort((a, b) => String(a.date).localeCompare(String(b.date)));

    return {
      trips: sortedTrips,
      totalDistance,
      totalTime,
      totalPoints,
    };
  }, [dailyTrips, rides, sortOrder]);

  // Group trips by date
  const tripsByDate = useMemo(() => {
    const grouped = {};

    processedTrips.trips.forEach(trip => {
      const dateKey = trip.startTime.toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: trip.startTime,
          trips: [],
          totalDistance: 0,
          totalDuration: 0
        };
      }
      grouped[dateKey].trips.push(trip);
      grouped[dateKey].totalDistance += trip.distance;
      grouped[dateKey].totalDuration += trip.movingTimeMs;
    });

    // Filter by date range
    const now = new Date();
    const filtered = {};

    Object.keys(grouped).forEach(dateKey => {
      const tripDate = new Date(dateKey);
      const diffDays = Math.floor((now - tripDate) / (1000 * 60 * 60 * 24));

      let include = false;
      switch (dateFilter) {
        case 'today':
          include = diffDays === 0;
          break;
        case 'week':
          include = diffDays <= 7;
          break;
        case 'month':
          include = diffDays <= 30;
          break;
        default:
          include = true;
      }

      if (include) {
        filtered[dateKey] = grouped[dateKey];
      }
    });

    return filtered;
  }, [processedTrips.trips, dateFilter]);

  // Toggle date expansion
  const toggleDate = (dateKey) => {
    setExpandedDates(prev => ({
      ...prev,
      [dateKey]: !prev[dateKey]
    }));
  };

  // Format duration
  const formatDuration = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Format time
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Handle play trip
  const handlePlayTrip = async (trip) => {
    setSelectedTrip(trip.id);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("date", trip.date);
      if (trip?.startTime) params.set("startTs", new Date(trip.startTime).toISOString());
      if (trip?.endTime) params.set("endTs", new Date(trip.endTime).toISOString());
      const url = `${baseUrl}/history/${deviceId}/playback?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "x-auth-token": token || "",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const route = Array.isArray(data?.points) ? data.points : [];
      if (!route.length) {
        setFetchError("No playback points for selected date");
        return;
      }
      const first = route[0];
      const last = route[route.length - 1];

      const playbackData = {
        id: trip.id,
        deviceId,
        date: formatDate(trip.startTime),
        startTime: formatTime(first?.timestamp || trip.startTime),
        endTime: formatTime(last?.timestamp || trip.endTime),
        durationSeconds: Number(data?.stats?.movingTime || 0) + Number(data?.stats?.idleTime || 0),
        durationLabel: formatDuration((Number(data?.stats?.movingTime || 0) + Number(data?.stats?.idleTime || 0)) * 1000),
        distance: Number(data?.stats?.totalDistanceRawKm ?? trip.distance).toFixed(2),
        maxSpeed: Number(data?.stats?.maxSpeedRawKmh ?? trip.maxSpeed).toFixed(0),
        avgSpeed: Number(data?.stats?.avgSpeedRawKmh ?? trip.avgSpeed).toFixed(0),
        fuelUsed: '0',
        route: route.map((p) => ({
          lat: Number(p.lat),
          lng: Number(p.lng),
          speed: Number(p.speed || 0),
          heading: Number(p.heading ?? p.course ?? p.bearing ?? 0),
          course: Number(p.course ?? 0),
          bearing: Number(p.bearing ?? 0),
          accStatus: p.accStatus ?? p.acc ?? p.ignition ?? null,
          vehicleStatus: p.vehicleStatus ?? p.status ?? null,
          timestamp: p.timestamp,
          satellites: Number(p.satellites || 0),
        })),
        startLocation: { lat: Number(first?.lat), lng: Number(first?.lng) },
        endLocation: { lat: Number(last?.lat), lng: Number(last?.lng) },
      };

      onPlayTrip(playbackData);
    } catch (err) {
      setFetchError(err?.message || "Failed to load playback");
    }
  };

  if (!deviceId) return null;

  const totalKm = processedTrips.totalDistance;

  return (
    <div className="trip-history-panel">
      {/* Header */}
      <div className="thp-header">
        <div className="thp-header-content">
          <div className="thp-title">
            <Route size={20} />
            <h3>Trip History</h3>
          </div>
          <span className="thp-device-name">{deviceName || "Device"}</span>
        </div>
        <button className="thp-close" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      {/* Stats Summary */}
      <div className="thp-stats">
        <div className="thp-stat-card primary">
          <TrendingUp size={20} />
          <div className="stat-content">
            <span className="stat-value">{totalKm.toFixed(1)}</span>
            <span className="stat-label">Total KM</span>
          </div>
        </div>
        <div className="thp-stat-card">
          <Activity size={20} />
          <div className="stat-content">
            <span className="stat-value">{processedTrips.trips.length}</span>
            <span className="stat-label">Trips</span>
          </div>
        </div>
        <div className="thp-stat-card">
          <Clock size={20} />
          <div className="stat-content">
            <span className="stat-value">{formatDuration(processedTrips.totalTime)}</span>
            <span className="stat-label">Total Time</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="thp-filters">
        <div className="date-filter">
          <Filter size={14} />
          <select 
            value={dateFilter} 
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
        <div className="sort-filter">
          <button 
            className={`sort-btn ${sortOrder === 'desc' ? 'active' : ''}`}
            onClick={() => setSortOrder('desc')}
          >
            Latest First
          </button>
          <button 
            className={`sort-btn ${sortOrder === 'asc' ? 'active' : ''}`}
            onClick={() => setSortOrder('asc')}
          >
            Oldest First
          </button>
        </div>
        <button 
          className="refresh-btn" 
          onClick={fetchHistory}
          disabled={historyLoading}
        >
          <RefreshCw size={14} className={historyLoading ? 'spinning' : ''} />
        </button>
      </div>

      {/* Trip List */}
      <div className="thp-trip-list">
        {historyLoading ? (
          <div className="thp-loading">
            <RefreshCw size={24} className="spinning" />
            <p>Loading trip history...</p>
          </div>
        ) : fetchError ? (
          <div className="thp-empty">
            <MapPin size={40} />
            <p>History fetch error</p>
            <small>{fetchError}</small>
          </div>
        ) : Object.keys(tripsByDate).length === 0 ? (
          <div className="thp-empty">
            <MapPin size={40} />
            <p>No trips found</p>
            <small>No location history available for this vehicle</small>
          </div>
        ) : (
          Object.keys(tripsByDate)
            .sort((a, b) => sortOrder === 'desc' ? b.localeCompare(a) : a.localeCompare(b))
            .map(dateKey => {
              const dateData = tripsByDate[dateKey];
              const isExpanded = expandedDates[dateKey] !== false; // Default expanded

              return (
                <div key={dateKey} className="date-group">
                  {/* Date Header */}
                  <div 
                    className="date-header"
                    onClick={() => toggleDate(dateKey)}
                  >
                    <div className="date-left">
                      {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      <Calendar size={16} />
                      <span className="date-text">{formatDate(dateData.date)}</span>
                    </div>
                    <div className="date-right">
                      <span className="date-stats">
                        {dateData.trips.length} trips • {dateData.totalDistance.toFixed(1)} km
                      </span>
                    </div>
                  </div>

                  {/* Trips for this date */}
                  {isExpanded && (
                    <div className="trips-container">
                      {dateData.trips.map((trip, index) => (
                        <div 
                          key={trip.id} 
                          className={`trip-card ${selectedTrip === trip.id ? 'selected' : ''}`}
                        >
                          <div className="trip-timeline">
                            <div className="trip-number">{index + 1}</div>
                            <div className="timeline-line" />
                          </div>
                          
                          <div className="trip-content">
                            <div className="trip-time-range">
                              <span className="time-start">{formatTime(trip.startTime)}</span>
                              <span className="time-separator">→</span>
                              <span className="time-end">{formatTime(trip.endTime)}</span>
                            </div>
                            
                            <div className="trip-metrics">
                              <div className="metric">
                                <TrendingUp size={12} />
                                <span>{trip.distance.toFixed(2)} km</span>
                              </div>
                              <div className="metric">
                                <Clock size={12} />
                                <span>{formatDuration(trip.endTime - trip.startTime)}</span>
                              </div>
                              <div className="metric">
                                <Gauge size={12} />
                                <span>Max {trip.maxSpeed.toFixed(0)} {units?.speedUnit || "km/h"}</span>
                              </div>
                            </div>
                            
                            <div className="trip-points-info">
                              <small>{trip.pointCount} GPS points</small>
                            </div>
                          </div>
                          
                          <button 
                            className="trip-play-btn"
                            onClick={() => handlePlayTrip(trip)}
                            title="Play this trip"
                          >
                            <Play size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
        )}
      </div>

      {/* Footer with total info */}
      <div className="thp-footer">
        <div className="footer-info">
          <MapPin size={14} />
          <span>
            {processedTrips.totalPoints || 0} location points recorded
          </span>
        </div>
        <button className="export-btn" title="Export History">
          <Download size={14} />
          Export
        </button>
      </div>
    </div>
  );
};

export default TripHistoryPanel;
